/**
 * App state (Concept §8.1). `currentDate` (UTC ms) is the single source of truth for time. The scene reads these
 * stores and changes them only through the actions below.
 */
import { derived, get, writable } from 'svelte/store';
import type { ScaleMode } from '../core/scaleProfiles';

export interface TimeRange {
  min: number;
  max: number;
}

export interface Playback {
  playing: boolean;
  /** Simulated seconds per real second (magnitude; the direction is `reverse`). */
  speed: number;
  /** Play backwards in time (S2-21). */
  reverse?: boolean;
}

const HOUR = 3600;
const DAY = 24 * HOUR;

/** Speed options (S1-11); the two hour speeds are experimental (spike Q6). */
export const SPEEDS = [
  { id: '1h', value: HOUR },
  { id: '6h', value: 6 * HOUR },
  { id: '1d', value: DAY },
  { id: '1w', value: 7 * DAY },
  { id: '1mo', value: 30.4375 * DAY },
  { id: '1y', value: 365.25 * DAY },
] as const;

export type SpeedId = (typeof SPEEDS)[number]['id'];

export const timeRange = writable<TimeRange>({ min: Date.UTC(1977, 7, 20, 15, 33), max: Date.now() });
export const currentDate = writable<number>(get(timeRange).min);
export const playback = writable<Playback>({ playing: false, speed: SPEEDS[4].value });
/** Height (px) of the flyby title card while it is shown, 0 otherwise — the scene frames below it. */
export const titleCardHeight = writable(0);
/** Space the detail panel takes (S3-16): its width as a side panel, its height as a sheet (px). */
export const panelInsets = writable<{ right: number; bottom: number }>({ right: 0, bottom: 0 });
/** Photo mode (S2-20): HUD and labels hidden, a small toolbar to save the picture. */
export const photoMode = writable(false);
/** The "About & credits" dialog is open (S4-05); it is loaded on first use. */
export const creditsOpen = writable(false);
/** True while the intro sequence runs (S2-12): the HUD stays hidden until it ends. */
export const introActive = writable(false);
/** The HUD stepped aside during playback (S4-15, core/autoHide.ts); any input brings it back. */
export const hudHidden = writable(false);
/** The detail sheet is open beyond its peek (half or full): someone is reading, the HUD stays. */
export const panelExpanded = writable(false);
/** The dock shows its extra rows on a phone (lanes, zoom, warp): the HUD stays. */
export const dockExpanded = writable(false);
/** A flyby event of the fleet (S3-14). */
export interface FlybyEvent {
  /** Closest approach, UTC ms. */
  time: number;
  spacecraft: string;
  mission: string;
  body: string;
}

/** Every flyby of every mission. */
export const flybyEvents = writable<FlybyEvent[]>([]);
/** A flyby (UTC ms) whose slow-motion the user skipped with one tap, or null. */
export const slowmoSkip = writable<number | null>(null);
export const scaleMode = writable<ScaleMode>('overview');
/** Camera focus: 'sun', a body id or a spacecraft id. */
export const focus = writable<string>('sun');
export const followTarget = writable<string | null>(null);
/** Selected object: a body or spacecraft id (camera focus and context). */
export const selection = writable<string | null>(null);
/**
 * The selected mission (D16): its detail panel is open, its trail is highlighted (D9) and its flybys get the
 * cinematic moments when nothing is followed (D13). Set by tapping a spacecraft, a timeline bar or an event.
 */
export const selectedMission = writable<string | null>(null);
/** Spacecraft id → mission id (registered at boot). */
const missionOfSpacecraft = new Map<string, string>();

export function registerSpacecraft(spacecraft: string, mission: string): void {
  missionOfSpacecraft.set(spacecraft, mission);
}

export function missionOf(spacecraft: string): string | undefined {
  return missionOfSpacecraft.get(spacecraft);
}
/** The body whose bubble contains the focused spacecraft (for the breadcrumb), written by the scene. */
export const focusContext = writable<string | null>(null);
/** Measured frames per second, written by the scene twice a second. */
export const fps = writable<number>(0);

function clampDate(ms: number): number {
  const { min, max } = get(timeRange);
  return Math.min(max, Math.max(min, ms));
}

export function setDate(ms: number): void {
  currentDate.set(clampDate(ms));
}

export function stepDate(deltaMs: number): void {
  setDate(get(currentDate) + deltaMs);
}

export function togglePlay(): void {
  playback.update((p) => {
    // Playing from the very end (or backwards from the very start) would stop at once: do not start.
    if (!p.playing && !p.reverse && get(currentDate) >= get(timeRange).max) return p;
    if (!p.playing && p.reverse && get(currentDate) <= get(timeRange).min) return p;
    return { ...p, playing: !p.playing };
  });
}

/** Flips the playback direction (S2-21: long-press on the play key, or R). */
export function toggleReverse(): void {
  playback.update((p) => ({ ...p, reverse: !p.reverse }));
}

export function setPlaying(playing: boolean): void {
  playback.update((p) => ({ ...p, playing }));
}

export function setSpeed(speed: number): void {
  playback.update((p) => ({ ...p, speed }));
}

/** Index of the speed detent closest to `speed` (log distance). */
export function speedIndex(speed: number): number {
  let best = 0;
  for (let i = 1; i < SPEEDS.length; i++) {
    if (Math.abs(Math.log(SPEEDS[i]!.value / speed)) < Math.abs(Math.log(SPEEDS[best]!.value / speed))) best = i;
  }
  return best;
}

/** Moves the throttle one detent up (+1) or down (−1), clamped to the available speeds. */
export function stepSpeed(dir: 1 | -1): void {
  const i = Math.min(Math.max(speedIndex(get(playback).speed) + dir, 0), SPEEDS.length - 1);
  setSpeed(SPEEDS[i]!.value);
}

export function setScaleMode(mode: ScaleMode): void {
  scaleMode.set(mode);
}

export function toggleScaleMode(): void {
  scaleMode.update((m) => (m === 'overview' ? 'realistic' : 'overview'));
}

/**
 * Closest-approach times (UTC ms) whose flyby moments play — slow-motion, cinematic camera, title card, haptics
 * (S2-11; Sprint 3, D13): only the followed spacecraft's, or the selected mission's when nothing is followed. With
 * six missions, 1979–1981 alone has seven giant-planet flybys; slowing down for all would make playback unusable.
 */
export const flybyTimes = derived([flybyEvents, followTarget, selectedMission], ([events, followed, mission]) =>
  events
    .filter((e) => (followed ? e.spacecraft === followed : mission !== null && e.mission === mission))
    .map((e) => e.time),
);

/** Focuses an object. Focusing anything other than the followed spacecraft ends follow mode. A spacecraft is
 * selected with its mission (D16). */
/**
 * Counts explicit focus requests for the object that is already focused (a chronology entry after a date jump): the
 * camera flies to it again, although `focus` itself does not change.
 */
export const refocus = writable(0);

export function focusOn(id: string): void {
  if (get(focus) === id) refocus.update((n) => n + 1);
  if (get(followTarget) !== id) followTarget.set(null);
  focus.set(id);
  selection.set(id);
  const mission = missionOfSpacecraft.get(id);
  if (mission) selectedMission.set(mission);
}

export function follow(id: string): void {
  if (get(focus) === id) refocus.update((n) => n + 1);
  focus.set(id);
  selection.set(id);
  followTarget.set(id);
  const mission = missionOfSpacecraft.get(id);
  if (mission) selectedMission.set(mission);
}

/** Selects a mission (timeline bar, list, deep link): the panel opens; the camera flies to its spacecraft. */
export function selectMission(mission: string | null): void {
  selectedMission.set(mission);
}

export function stopFollowing(): void {
  followTarget.set(null);
}
